import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import CustomDropdown from "../components/CustomDropdown";
import {
    eachDayOfInterval,
    isSunday,
    parseISO,
    isAfter,
    isBefore,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    startOfDay,
    endOfDay,
    format,
    getDate,
    getDaysInMonth,
} from "date-fns";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import { Check, X, Download } from "lucide-react";
import { fetchLeaveEntries } from "../utils/leaveUtils";
import { fetchCheckInRecordsForMonth, fetchCheckInRecordsForYear } from "../utils/checkInOutUtils";

export interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    joiningDate?: string;
}

interface CheckInTableProps {
    organizationId: string;
    users: User[];
    onBack: () => void;
    activeTab?: 'CheckIn' | 'TimeSheet';
    onTabChange?: (tab: 'CheckIn' | 'TimeSheet') => void;
}

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const CheckInTable: React.FC<CheckInTableProps> = ({
    organizationId,
    users,
    onBack,
    activeTab,
    onTabChange,
}) => {
    const now = new Date();
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const theme = getThemeClasses(isDark);
    const sortedUsers = React.useMemo(() => {
        return [...users].sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [users]);

    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedUserId, setSelectedUserId] = useState<string>("all");
    const [userStatusMap, setUserStatusMap] = useState<Record<string, Record<string, any>>>({});
    const [loading, setLoading] = useState(false);
    const [holidays, setHolidays] = useState<Record<string, any>>({});

    const [selectedDay, setSelectedDay] = useState<number>(0); // 0 = All
    const [showDetails, setShowDetails] = useState(false);

    // Generate year options
    const yearOptions = [];
    const currentYear = now.getFullYear();
    for (let year = 2025; year <= currentYear; year++) {
        yearOptions.push(year);
    }

    // Generate day options based on month/year
    const dayOptions = [{ value: "0", label: "All Days" }];
    if (selectedMonth !== 0) {
        const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
        for (let i = 1; i <= daysInMonth; i++) {
            dayOptions.push({ value: i.toString(), label: i.toString() });
        }
    }

    // Effect to reset selectedDay if invalid for new month
    useEffect(() => {
        if (selectedMonth !== 0) {
            const daysInM = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
            if (selectedDay > daysInM) setSelectedDay(0);
        } else {
            if (selectedDay !== 0) setSelectedDay(0);
        }
    }, [selectedMonth, selectedYear]);

    // Fetch holidays
    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                const holidaysCol = collection(db, "organization", organizationId, "holidays");
                const holidaysSnap = await getDocs(holidaysCol);
                const holidaysData: Record<string, any> = {};
                holidaysSnap.forEach((doc) => {
                    const data = doc.data();
                    holidaysData[doc.id] = { id: doc.id, date: data.date, reason: data.reason };
                });
                setHolidays(holidaysData);
            } catch (error) {
                console.error("❌ Error fetching holidays:", error);
                setHolidays({});
            }
        };
        fetchHolidays();
    }, [organizationId]);

    const fetchData = async () => {
        if (!organizationId || users.length === 0) return;
        setLoading(true);

        const statusMap: Record<string, Record<string, any>> = {};

        const userPromises = users.map(async (u) => {
            try {
                // Fetch Check-ins from partitioned month subcollections.
                // Always scope the fetch to the SELECTED period, never to the current
                // date, otherwise past months resolve to partitions that were never read.
                const checkInRecords = selectedMonth === 0
                    ? await fetchCheckInRecordsForYear(organizationId, u.id, selectedYear)
                    : await fetchCheckInRecordsForMonth(organizationId, u.id, selectedYear, selectedMonth);
                const checkInData = checkInRecords.reduce((acc, data) => {
                    if (data.checkInTime) {
                        try {
                            const dateKey = format(parseISO(data.checkInTime), "yyyy-MM-dd");
                            acc[dateKey] = data;
                        } catch (e) { }
                    }
                    return acc;
                }, {} as Record<string, any>);

                // Fetch Leaves
                const leaveEntries = await fetchLeaveEntries(organizationId, u.id, selectedYear);

                // A pending leave still marks the day as leave. Filtering to
                // approved only left those days as a bare "Absent" with no
                // reason, so they showed as neither a leave in Show Details nor
                // in the Casual/Sick/Unpaid tallies. Only rejected is dropped.
                const isApprovedStatus = (raw: any) => {
                    const status = (raw || "").toLowerCase();
                    return status === "approved" || status === "approve";
                };
                const isRejectedStatus = (raw: any) => {
                    const status = (raw || "").toLowerCase();
                    return status === "rejected" || status === "reject";
                };

                const relevantLeaves = leaveEntries.filter(
                    (e: any) => !isRejectedStatus(e.approvalStatus)
                );

                const userStatus: Record<string, any> = {};

                // Use the days calculated in the render body or generate them here for the map
                // To be safe, we calculate them again here or pass them in. 
                // Let's generate the full range needed for the map.
                let dataStart = startOfMonth(new Date(selectedYear, selectedMonth === 0 ? 0 : selectedMonth - 1));
                let dataEnd = selectedMonth === 0 ? endOfYear(dataStart) : endOfMonth(dataStart);
                if (selectedYear === now.getFullYear() && dataEnd > now) dataEnd = now;

                const daysInterval = eachDayOfInterval({ start: dataStart, end: dataEnd });

                daysInterval.forEach(day => {
                    const dateKey = format(day, "yyyy-MM-dd");
                    const isSun = isSunday(day);
                    const isHol = !!holidays[dateKey];

                    // Determine if user joined yet
                    let hasJoined = true;
                    if (u.joiningDate) {
                        try {
                            const joinDate = parseISO(u.joiningDate);
                            if (isBefore(day, joinDate)) hasJoined = false;
                        } catch { }
                    }

                    if (!hasJoined) {
                        userStatus[dateKey] = { status: "NA" };
                        return;
                    }

                    const checkInDoc = checkInData[dateKey];
                    if (checkInDoc) {
                        let checkInTimeStr = "-";
                        let checkOutTimeStr = "-";
                        try {
                            if (checkInDoc.checkInTime) {
                                const cIn = checkInDoc.checkInTime.toDate ? checkInDoc.checkInTime.toDate() : parseISO(checkInDoc.checkInTime);
                                checkInTimeStr = format(cIn, "hh:mm a");
                            }

                            if (checkInDoc.checkOutTime) {
                                const rawOut = checkInDoc.checkOutTime;
                                if (checkInDoc.markedAsAbsent) {
                                    checkOutTimeStr = "Auto Checkout";
                                } else {
                                    try {
                                        const cOut = rawOut.toDate ? rawOut.toDate() : parseISO(rawOut);
                                        checkOutTimeStr = format(cOut, "hh:mm a");
                                    } catch {
                                        checkOutTimeStr = rawOut.toString();
                                    }
                                }
                            } else {
                                const todayStr = format(new Date(), "yyyy-MM-dd");
                                if (dateKey === todayStr) {
                                    checkOutTimeStr = "Working";
                                }
                            }
                        } catch (e) {
                            console.error("Error formatting times for", dateKey, e);
                            checkOutTimeStr = checkInDoc.checkOutTime || "-";
                        }

                        userStatus[dateKey] = {
                            status: "Present",
                            checkIn: checkInTimeStr,
                            checkOut: checkOutTimeStr,
                            reason: checkInDoc.reason || "",
                            checkInAddress: checkInDoc.checkInAddress || "-",
                            checkOutAddress: checkInDoc.checkOutAddress || "-",
                            locationStatus: checkInDoc.locationStatus || null,
                            checkOutLocationStatus: checkInDoc.checkOutLocationStatus || null,
                            taskUpdate: checkInDoc.taskUpdate || "",
                            workMode: checkInDoc.workMode || "Office"
                        };
                    } else {
                        // Check if on leave
                        const coversDay = (l: any) => {
                            try {
                                if (!l.fromDate || !l.toDate) return false;
                                const lStart = startOfDay(parseISO(l.fromDate));
                                const lEnd = endOfDay(parseISO(l.toDate));
                                const current = parseISO(dateKey);
                                return (current >= lStart && current <= lEnd);
                            } catch { return false; }
                        };

                        // An approved leave wins over a pending one on the same
                        // day, so a confirmed day never reads as "(Pending)".
                        const leavesOnDay = relevantLeaves.filter(coversDay);
                        const leave =
                            leavesOnDay.find((l: any) => isApprovedStatus(l.approvalStatus)) ||
                            leavesOnDay[0];

                        if (leave) {
                            let leaveReason = leave.reason || "";
                            if (leave.leaveType === "Unpaid" || leave.leaveType === "unpaid") {
                                leaveReason = "Unpaid leave";
                            }
                            if (!isApprovedStatus(leave.approvalStatus)) {
                                leaveReason = `${leaveReason || "Leave"} (Pending)`;
                            }
                            userStatus[dateKey] = {
                                status: "Absent",
                                checkIn: "-",
                                checkOut: "-",
                                reason: leaveReason
                            };
                        } else if (isSun || isHol) {
                            userStatus[dateKey] = { status: "Off" };
                        } else {
                            userStatus[dateKey] = {
                                status: "Absent",
                                checkIn: "-",
                                checkOut: "-",
                                reason: ""
                            };
                        }
                    }
                });
                statusMap[u.id] = userStatus;

            } catch (error) {
                console.error(`Error fetching for ${u.id}`, error);
            }
        });

        await Promise.all(userPromises);
        setUserStatusMap(statusMap);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth, selectedYear, organizationId, users, holidays, selectedDay]);

    // Calculate days synchronously in each render
    const yearNum = Number(selectedYear);
    const monthNum = Number(selectedMonth);
    const dayNumVal = Number(selectedDay);
    const daysList: Date[] = [];

    if (!isNaN(yearNum) && !isNaN(monthNum) && monthNum !== 0) {
        const daysInM = getDaysInMonth(new Date(yearNum, monthNum - 1));
        const lastDayToShow = (monthNum === now.getMonth() + 1 && yearNum === now.getFullYear()) ? now.getDate() : daysInM;

        for (let i = 1; i <= lastDayToShow; i++) {
            if (dayNumVal === 0 || i === dayNumVal) {
                daysList.push(new Date(yearNum, monthNum - 1, i));
            }
        }
    } else if (monthNum === 0) {
        const endMonth = yearNum === now.getFullYear() ? now.getMonth() : 11;
        for (let m = 0; m <= endMonth; m++) {
            const dInM = getDaysInMonth(new Date(yearNum, m));
            const dToShow = (m === now.getMonth() && yearNum === now.getFullYear()) ? now.getDate() : dInM;
            for (let i = 1; i <= dToShow; i++) {
                daysList.push(new Date(yearNum, m, i));
            }
        }
    }

    // Helper to get initials
    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    // Calculate Summary Stats
    const getUserStats = (userId: string) => {
        const userDays = userStatusMap[userId] || {};
        const days = Object.values(userDays);
        const statuses = days.map((d: any) => d.status);
        return {
            present: statuses.filter(s => s === "Present").length,
            absent: statuses.filter(s => s === "Absent").length,
            off: statuses.filter(s => s === "Off").length,
            autoCheckout: days.filter((d: any) => d.checkOut === "Auto Checkout").length,
            casual: days.filter((d: any) => d.status === "Absent" && /casual/i.test(d.reason || "")).length,
            sick: days.filter((d: any) => d.status === "Absent" && /sick/i.test(d.reason || "")).length,
            unpaid: days.filter((d: any) => d.status === "Absent" && /unpaid/i.test(d.reason || "")).length,
        };
    };

    // Export to Excel
    const handleExport = async () => {
        // Dynamically import xlsx and file-saver
        const XLSX = await import("xlsx-js-style");
        const { saveAs } = await import("file-saver");

        const exportData: any[] = [];
        const daysToProcess = daysList;

        if (selectedUserId !== "all") {
            // --- VERTICAL SINGLE USER EXPORT ---
            const user = users.find(u => u.id === selectedUserId);
            daysToProcess.forEach(day => {
                const key = format(day, "yyyy-MM-dd");
                const dayData = userStatusMap[selectedUserId]?.[key];
                const status = dayData?.status;

                exportData.push({
                    "Date": format(day, "dd MMM yyyy"),
                    "Day": format(day, "EEEE"),
                    "Status": status === "Present" ? "✔" : (status === "Absent" ? "✖" : (status || "-")),
                    "In Time": dayData?.checkIn || "-",
                    "Out Time": dayData?.checkOut || "-",
                    "Reason": dayData?.reason || "-",
                    "CheckIn Status": dayData?.locationStatus || (dayData?.checkInAddress && dayData?.checkInAddress !== "-" ? "not recorded" : "-"),
                    "CheckIn Address": dayData?.checkInAddress || "-",
                    "CheckOut Status": dayData?.checkOutLocationStatus || (dayData?.checkOutAddress && dayData?.checkOutAddress !== "-" ? "not recorded" : "-"),
                    "CheckOut Address": dayData?.checkOutAddress || "-",
                    "Task Detail": dayData?.taskUpdate || "-",
                    "Auto Checkout": dayData?.checkOut === "Auto Checkout" ? "Yes" : "No"
                });
            });
        } else {
            // --- HORIZONTAL MULTI-USER EXPORT ---
            // 1. Create Summary Row (Total P | A)
            const summaryRow: any = { "Employee Name": "Total (Present | Absent)" };
            daysToProcess.forEach(day => {
                const dateKey = format(day, "yyyy-MM-dd");
                const displayKey = format(day, "dd MMM");
                let pCount = 0;
                let aCount = 0;
                sortedUsers.forEach(u => {
                    const s = userStatusMap[u.id]?.[dateKey]?.status;
                    if (s === "Present") pCount++;
                    if (s === "Absent") aCount++;
                });

                const countStr = `${pCount} | ${aCount}`;
                if (showDetails) {
                    summaryRow[`${displayKey}`] = countStr;
                    summaryRow[`${displayKey}_In`] = "-";
                    summaryRow[`${displayKey}_Out`] = "-";
                    summaryRow[`${displayKey}_Reason`] = "-";
                } else {
                    summaryRow[`${displayKey}`] = countStr;
                }
            });
            if (selectedDay === 0) {
                summaryRow["Total Present"] = "-";
                summaryRow["Total Absent"] = "-";
                summaryRow["Total Auto Checkout"] = "-";
            }
            exportData.push(summaryRow);

            // 2. Create Details Sub-Header Row (only if showDetails)
            if (showDetails) {
                const detailsRow: any = { "Employee Name": "DETAILS" };
                daysToProcess.forEach(day => {
                    const displayKey = format(day, "dd MMM");
                    detailsRow[`${displayKey}`] = "Status";
                    detailsRow[`${displayKey}_In`] = "In";
                    detailsRow[`${displayKey}_Out`] = "Out";
                    detailsRow[`${displayKey}_Reason`] = "Reason";
                    detailsRow[`${displayKey}_TaskUpdate`] = "Task Detail";
                });
                if (selectedDay === 0) {
                    detailsRow["Total Present"] = "-";
                    detailsRow["Total Absent"] = "-";
                    detailsRow["Total Auto Checkout"] = "-";
                }
                exportData.push(detailsRow);
            }

            // 3. Add Employee Rows
            sortedUsers.forEach(user => {
                const row: any = {
                    "Employee Name": `${user.firstName} ${user.lastName}`,
                };

                daysToProcess.forEach(day => {
                    const key = format(day, "yyyy-MM-dd");
                    const displayKey = format(day, "dd MMM");
                    const dayData = userStatusMap[user.id]?.[key];
                    const status = dayData?.status;

                    if (showDetails) {
                        row[`${displayKey}`] = status === "Present" ? "✔" : (status === "Absent" ? "✖" : (status || "-"));
                        row[`${displayKey}_In`] = dayData?.checkIn || "-";
                        row[`${displayKey}_Out`] = dayData?.checkOut || "-";
                        row[`${displayKey}_Reason`] = dayData?.reason || "-";
                        row[`${displayKey}_TaskUpdate`] = dayData?.taskUpdate || "-";
                    } else {
                        row[`${displayKey}`] = status === "Present" ? "✔" : (status === "Absent" ? "✖" : (status || "-"));
                    }
                });

                if (selectedDay === 0) {
                    const stats = getUserStats(user.id);
                    row["Total Present"] = stats.present;
                    row["Total Absent"] = stats.absent;
                    row["Total Auto Checkout"] = stats.autoCheckout;
                }

                exportData.push(row);
            });
        }

        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Apply styles
        const range = XLSX.utils.decode_range(worksheet['!ref'] as string);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if (!worksheet[cell_ref]) continue;

                const cell = worksheet[cell_ref];

                // Style Header
                if (R === 0) {
                    cell.s = {
                        font: { bold: true },
                        alignment: { horizontal: "center", vertical: "center" }
                    };
                } else {
                    // Style Data Cells
                    if (cell.v === "✔") {
                        cell.s = {
                            font: { color: { rgb: "00aa00" }, bold: true }, // Green
                            alignment: { horizontal: "center", vertical: "center" }
                        };
                    } else if (cell.v === "✖") {
                        cell.s = {
                            font: { color: { rgb: "ff0000" }, bold: true }, // Red
                            alignment: { horizontal: "center", vertical: "center" }
                        };
                    } else {
                        cell.s = { alignment: { horizontal: "center", vertical: "center" } };
                    }
                }
            }
        }

        // Auto-width columns
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({ wch: Math.max(key.length, 18) }));
        worksheet["!cols"] = colWidths;

        // Apply Merges
        const merges: any[] = [];
        if (selectedUserId === "all") {
            const maxHeaderR = showDetails ? 2 : 1;
            if (showDetails) {
                daysToProcess.forEach((_, idx) => {
                    const startCol = 1 + idx * 4;
                    const endCol = startCol + 3;
                    merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: endCol } });
                    merges.push({ s: { r: 1, c: startCol }, e: { r: 1, c: endCol } });
                });
            }
            if (selectedDay === 0) {
                const numKeys = Object.keys(exportData[0]).length;
                merges.push({ s: { r: 0, c: numKeys - 3 }, e: { r: maxHeaderR, c: numKeys - 3 } });
                merges.push({ s: { r: 0, c: numKeys - 2 }, e: { r: maxHeaderR, c: numKeys - 2 } });
                merges.push({ s: { r: 0, c: numKeys - 1 }, e: { r: maxHeaderR, c: numKeys - 1 } });
            }
        }
        worksheet["!merges"] = merges;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "CheckIn Data");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

        const monthLabel = selectedMonth === 0 ? "AllMonths" : selectedMonth;
        saveAs(blob, `CheckIn_Report_${selectedYear}_${monthLabel}${selectedDay > 0 ? `_Day${selectedDay}` : ''}.xlsx`);
    };


    return (
        <div className={`flex flex-col w-full h-full transition-colors duration-200 ${isDark ? 'bg-[#0B0B0F] text-white' : 'bg-transparent text-slate-900'}`}>
            <style>
                {`
                    .gold-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: ${isDark ? '#B59143' : '#FBD282'} transparent;
                    }
                    .gold-scrollbar::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    .gold-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .gold-scrollbar::-webkit-scrollbar-thumb {
                        background: linear-gradient(to bottom, #FBD282, #B59143);
                        border-radius: 99px;
                    }
                    .gold-scrollbar::-webkit-scrollbar-thumb:hover {
                         background: linear-gradient(to bottom, #E8C16C, #A7843D);
                    }
                `}
            </style>

            {/* Header Section */}
            <div className={`flex justify-between items-center p-4 transition-all duration-300 ${theme.headerGradient} rounded-md mb-6 border ${theme.border}`}>
                {isDark && (
                    <>
                        <span className={theme.headerOverlayGlow} />
                        <span className={theme.headerOverlayTopLine} />
                        <span className={theme.headerOverlayBottomGlow} />
                        <span className={theme.headerOverlaySweep} />
                    </>
                )}
                <div className="flex flex-col gap-1 relative z-10">
                    <h1 className={`text-2xl font-bold ${theme.text}`}>Overview</h1>
                    <p className={`text-xs ${theme.textMuted}`}>
                        Monitor all daily check-ins and timesheets for employees.
                    </p>
                </div>
                {/* Tab Switch */}
                <div className={`relative z-10 flex items-center p-1 rounded-lg border ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-orange-200'}`}>
                    <button
                        onClick={() => onTabChange?.('CheckIn')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'CheckIn' || !activeTab
                            ? (isDark ? 'bg-amber-500/20 text-amber-500' : 'bg-orange-100 text-orange-700')
                            : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-500 hover:text-slate-700')
                            }`}
                    >
                        CheckIns
                    </button>
                    <button
                        onClick={() => onTabChange?.('TimeSheet')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'TimeSheet'
                            ? (isDark ? 'bg-amber-500/20 text-amber-500' : 'bg-orange-100 text-orange-700')
                            : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-slate-500 hover:text-slate-700')
                            }`}
                    >
                        TimeSheets
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <CustomDropdown
                        value={selectedUserId}
                        onChange={(value) => setSelectedUserId(value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-64"
                        options={[{ value: "all", label: "All Users" }, ...sortedUsers.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))]}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <CustomDropdown
                        value={selectedDay.toString()}
                        onChange={(value) => setSelectedDay(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg"
                        options={dayOptions}
                    />
                    <CustomDropdown
                        value={selectedMonth.toString()}
                        onChange={(value) => setSelectedMonth(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-32"
                        options={[
                            { value: "0", label: "All Months" },
                            ...MONTHS.map((m, i) => ({ value: (i + 1).toString(), label: m })).filter(opt => {
                                if (selectedYear === now.getFullYear()) return (parseInt(opt.value) - 1) <= now.getMonth();
                                return true;
                            })
                        ]}
                    />
                    <CustomDropdown
                        value={selectedYear.toString()}
                        onChange={(value) => setSelectedYear(+value)}
                        isDark={isDark}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg w-24"
                        options={yearOptions.map(y => ({ value: y.toString(), label: y.toString() }))}
                    />
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all shadow-sm ${showDetails
                            ? (isDark ? 'bg-amber-500/20 text-amber-500 border border-amber-500/50' : 'bg-orange-100 text-orange-700 border border-orange-200')
                            : (isDark ? 'bg-[#1A1C1F] text-gray-400 border border-[#2E2F33] hover:text-gray-200' : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-800')
                            }`}
                    >
                        {showDetails ? "Hide Details" : "Show Details"}
                    </button>
                    <button
                        onClick={handleExport}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${isDark ? 'bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white hover:from-[#E8C16C] hover:to-[#A7843D]' : 'bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white hover:from-[#E8C16C] hover:to-[#A7843D]'}`}
                    >
                        <Download size={16} />
                        Export
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Loading attendance data...</p>
                    </div>
                ) : (
                    <div className={`flex flex-col rounded-xl border overflow-hidden max-h-full ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-slate-200 bg-white'}`}>
                        <div className="flex-1 overflow-auto relative gold-scrollbar">
                            {selectedUserId === "all" ? (
                                <table className="border-collapse w-full min-w-max text-sm text-left">
                                    <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#1A1C1F] text-gray-400' : 'bg-white text-slate-500'}`}>
                                        <tr>
                                            <th className={`sticky left-0 z-20 px-6 py-2 font-semibold text-xs uppercase tracking-wider border-b border-r min-w-[200px] ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-slate-200'}`}>
                                                Employee
                                            </th>
                                            {/* Date Columns */}
                                            {daysList.map((day, idx) => {
                                                const isWeekend = isSunday(day);
                                                return (
                                                    <th key={idx} colSpan={showDetails ? 4 : 1} className={`px-2 py-2 text-center border-b border-r min-w-[50px] last:border-r-0 ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-slate-200 bg-white'} ${isWeekend ? (isDark ? 'bg-[#232529]' : 'bg-slate-50') : ''}`}>
                                                        <div className="flex flex-col items-center">
                                                            {monthNum === 0 && (
                                                                <span className={`text-[9px] font-bold uppercase ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>{format(day, 'MMM')}</span>
                                                            )}
                                                            <span className={`text-[10px] font-bold uppercase mb-0.5 ${isWeekend ? 'text-rose-500' : ''}`}>{format(day, 'EEE')}</span>
                                                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{getDate(day)}</span>
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                            {selectedDay === 0 && (
                                                <>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[80px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-cyan-500' : 'border-slate-200 bg-white text-cyan-600'}`}>Casual</th>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[80px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-purple-500' : 'border-slate-200 bg-white text-purple-600'}`}>Sick</th>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[80px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-orange-500' : 'border-slate-200 bg-white text-orange-600'}`}>Unpaid</th>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[100px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-emerald-500' : 'border-slate-200 bg-white text-emerald-600'}`}>Present</th>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[100px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-rose-500' : 'border-slate-200 bg-white text-rose-600'}`}>Absent</th>
                                                    <th className={`px-2 py-2 text-center border-b border-r min-w-[120px] ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-amber-500' : 'border-slate-200 bg-white text-amber-600'}`}>Auto Checkout</th>
                                                </>
                                            )}
                                        </tr>
                                        {/* Summary Row */}
                                        <tr className={`font-semibold border-b ${isDark ? 'bg-[#232529] border-[#2E2F33]' : 'bg-slate-50 border-slate-200'}`}>
                                            <td className={`sticky left-0 z-20 px-6 py-2 border-r text-xs ${isDark ? 'bg-[#232529] border-[#2E2F33] text-white' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                                                Total (Present | Absent)
                                            </td>
                                            {daysList.map((day, idx) => {
                                                const dateKey = format(day, "yyyy-MM-dd");
                                                let pCount = 0;
                                                let aCount = 0;
                                                users.forEach(u => {
                                                    const s = userStatusMap[u.id]?.[dateKey]?.status;
                                                    if (s === "Present") pCount++;
                                                    if (s === "Absent") aCount++;
                                                });

                                                return (
                                                    <td key={idx} colSpan={showDetails ? 4 : 1} className={`px-1 py-1 text-center border-r last:border-r-0 text-[10px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>
                                                        <div className="flex items-center justify-center gap-1">
                                                            <span className="text-emerald-600 font-bold">{pCount}</span>
                                                            <span className="text-slate-300">|</span>
                                                            <span className="text-rose-600 font-bold">{aCount}</span>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            {selectedDay === 0 && (
                                                <>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                    <td className={`px-2 py-2 border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></td>
                                                </>
                                            )}
                                        </tr>
                                        {showDetails && (
                                            <tr className={`text-[10px] uppercase font-bold tracking-wider border-b ${isDark ? 'bg-[#1A1C1F] text-gray-500' : 'bg-slate-50 text-slate-500'}`}>
                                                <th className={`sticky left-0 z-20 px-6 py-2 border-r ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-slate-50 border-slate-200'}`}>
                                                    Details
                                                </th>
                                                {daysList.map((day, idx) => (
                                                    <React.Fragment key={`h2-${idx}`}>
                                                        <th className={`px-1 py-2 text-center border-r min-w-[70px] text-[10px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Status</th>
                                                        <th className={`px-1 py-2 text-center border-r min-w-[70px] text-[10px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>In</th>
                                                        <th className={`px-1 py-2 text-center border-r min-w-[70px] text-[10px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Out</th>
                                                        <th className={`px-1 py-2 text-center border-r min-w-[100px] text-[10px] last:border-r-0 ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Reason</th>
                                                    </React.Fragment>
                                                ))}
                                                {selectedDay === 0 && (
                                                    <>
                                                        <th className={`px-1 py-2 border-r min-w-[80px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                        <th className={`px-1 py-2 border-r min-w-[80px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                        <th className={`px-1 py-2 border-r min-w-[80px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                        <th className={`px-1 py-2 border-r min-w-[90px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                        <th className={`px-1 py-2 border-r min-w-[90px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                        <th className={`px-1 py-2 border-r min-w-[110px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}></th>
                                                    </>
                                                )}
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody className={`divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-slate-100'}`}>
                                        {sortedUsers.map(user => (
                                            <tr key={user.id} className={`group transition-colors ${isDark ? 'hover:bg-[#232529]' : 'hover:bg-slate-50'}`}>
                                                <td className={`sticky left-0 z-10 px-6 py-2 border-r ${isDark ? 'bg-[#1A1C1F] group-hover:bg-[#232529] border-[#2E2F33]' : 'bg-white group-hover:bg-slate-50 border-slate-200'}`}>
                                                    <div>
                                                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.firstName} {user.lastName}</p>
                                                    </div>
                                                </td>

                                                {daysList.map((day, idx) => {
                                                    const key = format(day, "yyyy-MM-dd");
                                                    const dayData = userStatusMap[user.id]?.[key];
                                                    const status = dayData?.status;
                                                    const isWeekend = isSunday(day);
                                                    const isHol = !!holidays[key];

                                                    let cellContent = null;
                                                    let cellClass = "";

                                                    if (status === "Present") {
                                                        cellContent = <Check size={16} strokeWidth={3} className="text-emerald-600 dark:text-emerald-500 mx-auto" />;
                                                    } else if (status === "Absent") {
                                                        cellContent = <X size={16} strokeWidth={3} className="text-rose-600 dark:text-rose-500 mx-auto" />;
                                                    } else if (status === "Off") {
                                                        cellClass = isDark ? "bg-[#23252b] opacity-50" : "bg-slate-50";
                                                        cellContent = <span className="text-xs text-slate-300">-</span>;
                                                        if (isHol) {
                                                            cellContent = <span title={holidays[key]?.reason} className="text-[10px] px-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 font-bold">H</span>;
                                                        }
                                                    }

                                                    if (showDetails) {
                                                        return (
                                                            <React.Fragment key={`d-${idx}`}>
                                                                <td className={`px-1 py-1 text-center border-r min-w-[70px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'} ${cellClass} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                                    <div className="flex items-center justify-center w-full h-full">{cellContent}</div>
                                                                </td>
                                                                <td className={`px-1 py-1 text-center border-r text-[10px] min-w-[70px] ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-500'} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                                    {dayData?.checkIn || "-"}
                                                                </td>
                                                                <td className={`px-1 py-1 text-center border-r text-[10px] min-w-[70px] ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-500'} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                                    {dayData?.checkOut || "-"}
                                                                </td>
                                                                <td className={`px-1 py-1 text-center border-r last:border-r-0 text-[10px] min-w-[100px] ${isDark ? 'border-[#2E2F33] text-amber-500' : 'border-slate-100 text-amber-600'} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                                    {dayData?.reason || "-"}
                                                                </td>
                                                            </React.Fragment>
                                                        );
                                                    }

                                                    return (
                                                        <td key={idx} className={`px-1 py-1 text-center border-r last:border-r-0 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'} ${cellClass} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                            <div className="flex items-center justify-center w-full h-full">{cellContent}</div>
                                                        </td>
                                                    );
                                                })}

                                                {selectedDay === 0 && (
                                                    <>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-cyan-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).casual}
                                                        </td>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-purple-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).sick}
                                                        </td>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-orange-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).unpaid}
                                                        </td>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-emerald-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).present}
                                                        </td>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-rose-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).absent}
                                                        </td>
                                                        <td className={`px-1 py-1 text-center border-r font-bold text-amber-600 ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'}`}>
                                                            {getUserStats(user.id).autoCheckout}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="border-collapse w-full min-w-max text-sm text-left">
                                    <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#1A1C1F] text-gray-400' : 'bg-white text-slate-500'}`}>
                                        <tr className="uppercase text-[10px] font-bold tracking-wider">
                                            <th className={`px-6 py-4 border-b border-r min-w-[150px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Date</th>
                                            <th className={`px-4 py-4 text-center border-b border-r min-w-[80px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Status</th>
                                            <th className={`px-4 py-4 text-center border-b border-r min-w-[100px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Work Mode</th>
                                            <th className={`px-4 py-4 text-center border-b border-r min-w-[100px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>In Time</th>
                                            <th className={`px-4 py-4 text-center border-b border-r min-w-[100px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Out Time</th>
                                            <th className={`px-4 py-4 text-center border-b border-r min-w-[150px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Reason</th>
                                            <th className={`px-6 py-4 border-b border-r min-w-[250px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>CheckIn Address</th>
                                            <th className={`px-6 py-4 border-b border-r min-w-[250px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>CheckOut Address</th>
                                            <th className={`px-6 py-4 border-b min-w-[200px] ${isDark ? 'border-[#2E2F33]' : 'border-slate-200'}`}>Task Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-slate-100'}`}>
                                        {daysList.map((day, idx) => {
                                            const key = format(day, "yyyy-MM-dd");
                                            const dayData = userStatusMap[selectedUserId]?.[key];
                                            const status = dayData?.status;
                                            const isWeekend = isSunday(day);
                                            const isHol = !!holidays[key];

                                            let cellContent = null;
                                            let cellClass = "";

                                            if (status === "Present") {
                                                cellContent = <Check size={16} strokeWidth={3} className="text-emerald-600 dark:text-emerald-500 mx-auto" />;
                                            } else if (status === "Absent") {
                                                cellContent = <X size={16} strokeWidth={3} className="text-rose-600 dark:text-rose-500 mx-auto" />;
                                            } else if (status === "Off") {
                                                cellClass = isDark ? "bg-[#14161B] opacity-50" : "bg-slate-50";
                                                cellContent = <span className="text-xs text-slate-300">-</span>;
                                                if (isHol) {
                                                    cellContent = <span className="text-[10px] px-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 font-bold">H</span>;
                                                }
                                            }

                                            return (
                                                <tr key={idx} className={`group transition-colors ${isDark ? 'hover:bg-[#232529]' : 'hover:bg-slate-50'} ${isWeekend ? (isDark ? 'bg-[#181a1d]' : 'bg-slate-50/50') : ''}`}>
                                                    <td className={`px-6 py-3 border-r font-medium ${isDark ? 'border-[#2E2F33] text-gray-300' : 'border-slate-100 text-slate-700'}`}>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold uppercase text-amber-500">{format(day, 'EEEE')}</span>
                                                            <span className="text-sm">{format(day, 'dd MMM yyyy')}</span>
                                                        </div>
                                                    </td>
                                                    <td className={`px-4 py-3 text-center border-r ${isDark ? 'border-[#2E2F33]' : 'border-slate-100'} ${cellClass}`}>
                                                        {cellContent}
                                                    </td>
                                                    <td className={`px-4 py-3 text-center border-r text-sm ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-600'}`}>
                                                            <span>{dayData?.workMode || "-"}</span>
                                                    </td>
                                                    <td className={`px-4 py-3 text-center border-r text-sm ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-600'}`}>
                                                        {dayData?.checkIn || "-"}
                                                    </td>
                                                    <td className={`px-4 py-3 text-center border-r text-sm ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-600'}`}>
                                                        {dayData?.checkOut || "-"}
                                                    </td>
                                                    <td className={`px-4 py-3 text-center border-r text-xs ${isDark ? 'border-[#2E2F33] text-amber-500' : 'border-slate-100 text-amber-600'}`}>
                                                        {dayData?.reason || "-"}
                                                    </td>
                                                    <td className={`px-6 py-3 border-r text-[11px] leading-relaxed max-w-[250px] ${isDark ? 'border-[#2E2F33] text-gray-400' : 'border-slate-100 text-slate-500'}`}>
                                                        {dayData?.locationStatus && (
                                                            <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase mb-1.5 ${dayData.locationStatus === 'matched'
                                                                ? (isDark ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-100')
                                                                : (isDark ? 'bg-rose-500/10 text-rose-500 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-100')
                                                                }`}>
                                                                {dayData.locationStatus === 'matched' ? '✓ Matched' : '⚠ Not Matched'}
                                                            </div>
                                                        )}
                                                        <div className="line-clamp-2" title={dayData?.checkInAddress}>
                                                            {dayData?.checkInAddress || "-"}
                                                        </div>
                                                    </td>
                                                    <td className={`px-6 py-3 text-[11px] leading-relaxed max-w-[250px] ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                                        {dayData?.checkOutLocationStatus && (
                                                            <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase mb-1.5 ${dayData.checkOutLocationStatus === 'matched'
                                                                ? (isDark ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-100')
                                                                : (isDark ? 'bg-rose-500/10 text-rose-500 border border-rose-500/20' : 'bg-rose-50 text-rose-700 border border-rose-100')
                                                                }`}>
                                                                {dayData.checkOutLocationStatus === 'matched' ? '✓ Matched' : '⚠ Not Matched'}
                                                            </div>
                                                        )}
                                                        <div className="line-clamp-2" title={dayData?.checkOutAddress}>
                                                            {dayData?.checkOutAddress || "-"}
                                                        </div>
                                                    </td>
                                                    <td className={`px-6 py-3 text-[11px] leading-relaxed max-w-[200px] ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                                        <div className="line-clamp-3" title={dayData?.taskUpdate}>
                                                            {dayData?.taskUpdate || "-"}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CheckInTable;
